import { useCallback } from "react";

type Align = "start" | "center" | "end";

export type GridMathOptionsEm = {
  behavior?: ScrollBehavior;
  align?: Align;
  cellWidthEm: number;
  cellHeightEm: number;
  baseFontPxOverride?: number;
  scaleX?: number;
  scaleY?: number;
  maxCols?: number;
  maxRows?: number;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function getContainerFontPx(el: HTMLElement, override?: number): number {
  if (override && Number.isFinite(override)) return override;
  const cs = getComputedStyle(el);
  const fontPx = parseFloat(cs.fontSize);
  return Number.isFinite(fontPx) && fontPx > 0 ? fontPx : 16;
}

export function computeCellTopLeftEm(
  row: number,
  col: number,
  root: HTMLElement,
  {
    cellWidthEm,
    cellHeightEm,
    baseFontPxOverride,
  }: Pick<
    GridMathOptionsEm,
    "cellWidthEm" | "cellHeightEm" | "baseFontPxOverride"
  >
) {
  const fontPx = getContainerFontPx(root, baseFontPxOverride);
  const cellWidthPx = cellWidthEm * fontPx;
  const cellHeightPx = cellHeightEm * fontPx;

  const x = col * cellWidthPx;
  const y = row * cellHeightPx;
  return { x, y, cellWidthPx, cellHeightPx };
}

export function useGridMathScroller(
  containerRef: React.RefObject<HTMLDivElement | null>
) {
  const scrollToCell = useCallback(
    (row: number, col: number, opts: GridMathOptionsEm) => {
      const root = containerRef.current;
      if (!root) return;

      const {
        behavior = "smooth",
        align = "center",
        cellWidthEm,
        cellHeightEm,
        baseFontPxOverride,
        scaleX = 1,
        scaleY = 1,
        maxCols,
        maxRows,
      } = opts;

      const r = typeof maxRows === "number" ? clamp(row, 0, maxRows - 1) : row;
      const c = typeof maxCols === "number" ? clamp(col, 0, maxCols - 1) : col;

      const { x, y, cellWidthPx, cellHeightPx } = computeCellTopLeftEm(
        r,
        c,
        root,
        { cellWidthEm, cellHeightEm, baseFontPxOverride }
      );

      let targetLeft = x;
      let targetTop = y;

      if (align === "center") {
        targetLeft = x - (root.clientWidth - cellWidthPx) / 2;
        targetTop = y - (root.clientHeight - cellHeightPx) / 2;
      } else if (align === "end") {
        targetLeft = x - (root.clientWidth - cellWidthPx);
        targetTop = y - (root.clientHeight - cellHeightPx);
      }

      const left = targetLeft / (scaleX || 1);
      const top = targetTop / (scaleY || 1);

      const maxLeft = root.scrollWidth - root.clientWidth;
      const maxTop = root.scrollHeight - root.clientHeight;

      root.scrollTo({
        left: clamp(left, 0, Math.max(0, maxLeft)),
        top: clamp(top, 0, Math.max(0, maxTop)),
        behavior,
      });
    },
    [containerRef]
  );

  const scrollByCells = useCallback(
    (dRow: number, dCol: number, opts: GridMathOptionsEm) => {
      const root = containerRef.current;
      if (!root) return;

      const {
        behavior = "smooth",
        cellWidthEm,
        cellHeightEm,
        baseFontPxOverride,
        scaleX = 1,
        scaleY = 1,
      } = opts;

      const fontPx = getContainerFontPx(root, baseFontPxOverride);
      const cellWidthPx = cellWidthEm * fontPx;
      const cellHeightPx = cellHeightEm * fontPx;

      const deltaX = (dCol * cellWidthPx) / (scaleX || 1);
      const deltaY = (dRow * cellHeightPx) / (scaleY || 1);

      const targetLeft = root.scrollLeft + deltaX;
      const targetTop = root.scrollTop + deltaY;

      const maxLeft = root.scrollWidth - root.clientWidth;
      const maxTop = root.scrollHeight - root.clientHeight;

      root.scrollTo({
        left: clamp(targetLeft, 0, Math.max(0, maxLeft)),
        top: clamp(targetTop, 0, Math.max(0, maxTop)),
        behavior,
      });
    },
    [containerRef]
  );

  return { scrollToCell, scrollByCells };
}
